import { useMemo, useCallback } from 'react';
import moment from 'moment';

export function useCalendarMetrics({ events, currentDate, currentView, enablePeople }) {
  // Helper function to identify Outlook sync events that should be excluded from totals
  const isOutlookSyncEvent = useCallback((event) => {
    // Check if it's an Outlook sync event by timecode name
    if (event.timecodeName === '(SYNC) Outlook Sync') return true;
    
    // Check if ID starts with 'crm:' or 'projects:crm:'
    const idString = String(event.id || event.logId || '');
    if (idString.startsWith('crm:') || idString.startsWith('projects:crm:')) return true;
    
    return false;
  }, []);
  const isInCurrentView = useCallback((event) => {
    if (currentView === 'work_week') {
      const weekStart = moment(currentDate).startOf('week').add(0, 'days'); // Monday
      const weekEnd = moment(currentDate).startOf('week').add(4, 'days').endOf('day'); // Friday
      return moment(event.start).isBetween(weekStart, weekEnd, null, '[]');
    }

    const viewStart = moment(currentDate).startOf(
      currentView === 'month'
        ? 'month'
        : currentView === 'week'
        ? 'week'
        : 'day'
    );
    const viewEnd = moment(currentDate).endOf(
      currentView === 'month'
        ? 'month'
        : currentView === 'week'
        ? 'week'
        : 'day'
    );
    return moment(event.start).isBetween(viewStart, viewEnd, null, '[]');
  }, [currentDate, currentView]);

  const totalsCurrentView = useMemo(() => {
    let billable = 0;
    let nonBillable = 0;

    const sourceEvents = enablePeople
      ? events.filter(ev => !ev.isPeople && !ev.isPeopleLeave)
      : events.filter(ev => !ev.isPeopleLeave);

    sourceEvents.forEach(event => {
      if (isInCurrentView(event) && !isOutlookSyncEvent(event)) {
        const duration = (new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60);
        if (duration < 0) return;
        if (event.billingtype === 'Billable') billable += duration;
        else nonBillable += duration;
      }
    });

    return { billable, nonBillable };
  }, [events, enablePeople, isInCurrentView, isOutlookSyncEvent]);

  const totalsCurrentViewPeople = useMemo(() => {
    let billable = 0;
    let nonBillable = 0;

    events.forEach(ev => {
      if (ev.isPeople && isInCurrentView(ev)) {
        const duration = (new Date(ev.end) - new Date(ev.start)) / (1000 * 60 * 60);
        if (duration > 0) {
          if (ev.billingtype === 'Billable') billable += duration;
          else nonBillable += duration;
        }
      }
    });

    return { billable, nonBillable };
  }, [events, isInCurrentView]);

  const totalsYTD = useMemo(() => {
    let billable = 0;
    let nonBillable = 0;
    const startOfYear = moment().startOf('year');

    const sourceEvents = enablePeople
      ? events.filter(ev => !ev.isPeople && !ev.isPeopleLeave)
      : events.filter(ev => !ev.isPeopleLeave);

    sourceEvents.forEach(event => {
      if (moment(event.start).isSameOrAfter(startOfYear) && !isOutlookSyncEvent(event)) {
        const duration = (new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60);
        if (event.billingtype === 'Billable') billable += duration;
        else nonBillable += duration;
      }
    });

    return { billable, nonBillable };
  }, [events, enablePeople, isOutlookSyncEvent]);

  const leaveDaysYTD = useMemo(() => {
    const startOfYear = moment().startOf('year');
    return events.filter(ev => ev.isPeopleLeave && moment(ev.start).isSameOrAfter(startOfYear)).length;
  }, [events]);

  const totalsCurrentWeek = useMemo(() => {
    const weekStart = moment(currentDate).startOf('isoWeek');
    const weekEnd = moment(currentDate).endOf('isoWeek');
    let billable = 0, nonBillable = 0;
    const sourceEvents = enablePeople
      ? events.filter(ev => !ev.isPeople && !ev.isPeopleLeave)
      : events.filter(ev => !ev.isPeopleLeave);
    sourceEvents.forEach(ev => {
      if (isOutlookSyncEvent(ev)) return;
      if (!moment(ev.start).isBetween(weekStart, weekEnd, null, '[]')) return;
      const duration = (new Date(ev.end) - new Date(ev.start)) / (1000 * 60 * 60);
      if (duration <= 0) return;
      if (ev.billingtype === 'Billable') billable += duration;
      else nonBillable += duration;
    });
    return { billable, nonBillable };
  }, [events, enablePeople, isOutlookSyncEvent, currentDate]);

  const totalsCurrentWeekPeople = useMemo(() => {
    const weekStart = moment(currentDate).startOf('isoWeek');
    const weekEnd = moment(currentDate).endOf('isoWeek');
    let billable = 0, nonBillable = 0;
    events.forEach(ev => {
      if (!ev.isPeople) return;
      if (!moment(ev.start).isBetween(weekStart, weekEnd, null, '[]')) return;
      const duration = (new Date(ev.end) - new Date(ev.start)) / (1000 * 60 * 60);
      if (duration <= 0) return;
      if (ev.billingtype === 'Billable') billable += duration;
      else nonBillable += duration;
    });
    return { billable, nonBillable };
  }, [events, currentDate]);

  const leaveDaysCurrentWeek = useMemo(() => {
    const weekStart = moment(currentDate).startOf('isoWeek');
    const weekEnd = moment(currentDate).endOf('isoWeek');
    return events.filter(ev =>
      ev.isPeopleLeave && moment(ev.start).isBetween(weekStart, weekEnd, null, '[]')
    ).length;
  }, [events, currentDate]);

  const getLeaveCustomPeriod = useCallback((startDate, endDate) => {
    const [sy, sm, sd] = startDate.split('-').map(Number);
    const [ey, em, ed] = endDate.split('-').map(Number);
    const startMs = new Date(sy, sm - 1, sd, 0, 0, 0, 0).getTime();
    const endMs = new Date(ey, em - 1, ed, 23, 59, 59, 999).getTime();
    return events.filter(ev => {
      if (!ev.isPeopleLeave) return false;
      const t = new Date(ev.start).getTime();
      return t >= startMs && t <= endMs;
    }).length;
  }, [events]);

  const totalsYTDPeople = useMemo(() => {
    let billable = 0;
    let nonBillable = 0;
    const startOfYear = moment().startOf('year');

    events.forEach(ev => {
      if (ev.isPeople && moment(ev.start).isSameOrAfter(startOfYear)) {
        const duration = (new Date(ev.end) - new Date(ev.start)) / (1000 * 60 * 60);
        if (duration > 0) {
          if (ev.billingtype === 'Billable') billable += duration;
          else nonBillable += duration;
        }
      }
    });

    return { billable, nonBillable };
  }, [events]);

  const getDailyTotals = useCallback((date, context = 'projects') => {
    const dayStart = moment(date).startOf('day');
    const dayEnd = moment(date).endOf('day');

    let sourceEvents;
    if (context === 'people') {
      sourceEvents = events.filter(ev => ev.isPeople);
    } else if (context === 'leave') {
      const leaveOnDay = events.filter(
        ev => ev.isPeopleLeave && moment(ev.start).isBetween(dayStart, dayEnd, null, '[]')
      );
      const approvedDays = leaveOnDay.filter(ev => ev.leaveStatus === 'Approved').length;
      const pendingDays = leaveOnDay.filter(ev => ev.leaveStatus === 'Pending').length;
      return { billable: 0, nonBillable: 0, total: 0, approvedDays, pendingDays };
    } else {
      sourceEvents = events.filter(ev => !ev.isPeople && !ev.isPeopleLeave);
    }

    let billable = 0;
    let nonBillable = 0;
    sourceEvents.forEach(event => {
      if (moment(event.start).isBetween(dayStart, dayEnd, null, '[]') && !isOutlookSyncEvent(event)) {
        const duration = (new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60);
        if (duration <= 0) return;
        if (event.billingtype === 'Billable') billable += duration;
        else nonBillable += duration;
      }
    });
    return { billable, nonBillable, total: billable + nonBillable };
  }, [events, isOutlookSyncEvent]);

  const getCustomPeriodTotals = useCallback((startDate, endDate, context = 'projects') => {
    const [sy, sm, sd] = startDate.split('-').map(Number);
    const [ey, em, ed] = endDate.split('-').map(Number);
    const startMs = new Date(sy, sm - 1, sd, 0, 0, 0, 0).getTime();
    const endMs = new Date(ey, em - 1, ed, 23, 59, 59, 999).getTime();

    const sourceEvents = context === 'people'
      ? events.filter(ev => ev.isPeople)
      : (enablePeople
          ? events.filter(ev => !ev.isPeople && !ev.isPeopleLeave)
          : events.filter(ev => !ev.isPeopleLeave));

    let billable = 0;
    let nonBillable = 0;

    sourceEvents.forEach(ev => {
      if (isOutlookSyncEvent(ev)) return;

      const evStartMs = new Date(ev.start).getTime();
      const evEndMs = new Date(ev.end).getTime();
      if (evEndMs <= startMs || evStartMs >= endMs) return;

      const s = Math.max(evStartMs, startMs);
      const e = Math.min(evEndMs, endMs);
      const duration = (e - s) / (1000 * 60 * 60);

      if (duration > 0) {
        if (ev.billingtype === 'Billable') billable += duration;
        else nonBillable += duration;
      }
    });

    return { billable, nonBillable, total: billable + nonBillable };
  }, [events, enablePeople, isOutlookSyncEvent]);

  const getYearOverYearComparison = useCallback((startDate, endDate, context = 'projects') => {
    const currentPeriod = getCustomPeriodTotals(startDate, endDate, context);
    const prevYearStart = moment(startDate).subtract(1, 'year');
    const prevYearEnd = moment(endDate).subtract(1, 'year');
    const previousYearPeriod = getCustomPeriodTotals(
      prevYearStart.format('YYYY-MM-DD'),
      prevYearEnd.format('YYYY-MM-DD'),
      context
    );

    const totalDiff = currentPeriod.total - previousYearPeriod.total;
    const billableDiff = currentPeriod.billable - previousYearPeriod.billable;
    const totalPercentChange = previousYearPeriod.total > 0 
      ? ((totalDiff / previousYearPeriod.total) * 100) 
      : (currentPeriod.total > 0 ? 100 : 0);
    const billablePercentChange = previousYearPeriod.billable > 0 
      ? ((billableDiff / previousYearPeriod.billable) * 100) 
      : (currentPeriod.billable > 0 ? 100 : 0);

    return {
      current: currentPeriod,
      previous: previousYearPeriod,
      differences: {
        total: totalDiff,
        billable: billableDiff,
        totalPercent: totalPercentChange,
        billablePercent: billablePercentChange
      },
      hasData: currentPeriod.total > 0 || previousYearPeriod.total > 0
    };
  }, [getCustomPeriodTotals]);

  return {
    isInCurrentView,
    totalsCurrentView,
    totalsCurrentViewPeople,
    totalsYTD,
    totalsYTDPeople,
    leaveDaysYTD,
    totalsCurrentWeek,
    totalsCurrentWeekPeople,
    leaveDaysCurrentWeek,
    getDailyTotals,
    getCustomPeriodTotals,
    getLeaveCustomPeriod,
    getYearOverYearComparison,
  };
}